import Icon from 'components/icon'
import { clsx, styleAndCapture } from 'external/material'
import { FC, useState, Link, useContext } from 'external/react'
import { staticAssetLink } from 'helpers/url'
import { formatNumber } from 'helpers/utils'
import { TeacherContext } from 'teacher/context'

const StyledSubjectCard = styleAndCapture(Link, 'subject')(subjectCardStyles as any)

type SubjectCardProps = {
  className?: string
  id: string
  to: string
  name: string
  lessonCount: number
}

const subjectImage = (subject: string) => staticAssetLink('subject-logos', `${subject}.png`)

const SubjectCard: FC<SubjectCardProps> = props => {

    const {
        className = 'subject-card',
        id,
        to,
        name,
        lessonCount,
    } = props

    const {translate} = useContext(TeacherContext) as any
    const [imageError, setImageError] = useState(false)
  
  return (
    <StyledSubjectCard className={clsx('subject-card', className, { 'image-error': imageError })} to={to} subject={id}>
      <div className="card-image">
        <div className="filler" />
        <div className='image-container'>
            <img src={subjectImage(id)} alt={id} onError={(e) => { setImageError(true) }} />
        </div>
        {imageError && <div className="fallback-id">{name}</div>}
        <div className="card-title">{name}</div>
      </div>
    {lessonCount !== undefined && (
      <div className="lesson-count">
          <div className="icon">
            <Icon name='lesson-count' fill='#fff' />
          </div>
          <div className="label">{formatNumber(lessonCount)} {translate('lessons')}</div>
      </div>
    )}
    </StyledSubjectCard>
  )
}

export default SubjectCard

function subjectCardStyles(args: any) {

  const subject = getSubjectFromId(args.subject)

  return {
    color: '#fff',
    overflow: 'hidden',
    borderRadius: '16px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    background: `linear-gradient(to bottom, ${subject.startColor}, ${subject.endColor})`,
    aspectRatio: '1 / 1', // Forces perfect squares
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
    textAlign: 'center',
    position: 'relative',
    minHeight: 0, // Prevents flex items from overflowing
    '& > div': {
        width: '100%',
        padding: 0
    },
    '& > .card-image': {
        paddingTop: 10,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: 2,
        '& .filler': {
            flexGrow: 1
        }
    },
    '& .card-title': {
        fontSize: '1.2rem',
        fontWeight: 500,
        paddingBottom: 10
    },
    '& > .lesson-count': {
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        padding: 8,
        backgroundColor: `${subject.content}`,
        '& > div': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            '&.label': {
                paddingTop: 2,
                marginLeft: 8

            }
        }
    },
    '& img': {
        height: 140
    }
  }
}


const SUBJECT_MAP: Record<string, { startColor: string; endColor: string, content: string}> = {
  'C1': { 
    startColor: '#91F3E2', 
    endColor: '#1A9EA6',
    content: '#218487'
  },
  'C2': { 
    startColor: '#FCC663', 
    endColor: '#D06703' ,
    content: '#C05709'
  },
  'C3': { 
    startColor: '#D2A1F1', 
    endColor: '#B256B8' ,
    content: '#944599'
  },
  'C4': { 
    startColor: '#6DC5DE', 
    endColor: '#3B93BB',
    content: '#337C9C'
  },
  'C5': { 
    startColor: '#82F0B3', 
    endColor: '#459833',
    content: '#367F31'
  },
  'C6': { 
    startColor: '#FDDA35', 
    endColor: '#C8A818',
    content: '#A78C0F',
  },
  'C7': { 
    startColor: '#F19B9B', 
    endColor: '#B12323',
    content: '#9F2E2E'
  },
  'C8': { 
    startColor: '#F176CA', 
    endColor: '#AB2B81' ,
    content: '#9A2975'
  },
  'C9': { 
    startColor: '#C1C1C1', 
    endColor: '#6F6D6A',
    content: '#5F5B58'
  }
}

function getSubjectFromId(id: string): { startColor: string; endColor: string; content: string } {
  const upperCaseId = id.toUpperCase()
  
  if (SUBJECT_MAP[upperCaseId]) {
    return SUBJECT_MAP[upperCaseId]
  }
  return SUBJECT_MAP['C1'];
}